<template>
  <div>
    <add-or-update-member-status-modal
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      @refetchData="fetchAllData"
      @resetUpdateData="resetUpdateData"
    />

    <list-table
      :records="records"
      :columns="tableColumns"
      :total-records="totalRecords"
      :search-filter.sync="search"
      :current-page-number.sync="currentPage"
      :loading="loading"
      :error-message="errorMessage"
      :show-search="true"
      :show-filter-by-default="true"
    >
      <template v-if="can('member_status:CanAddNew')" #action_button>
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          @click="isAddOrUpdateActive = true"
        >
          <span class="align-middle text-lg">Add Member Status</span>
        </b-button>
      </template>

      <template #cell(created_by_user)="data">
        <div v-if="data.item.created_by_user" class="d-flex flex-column">
          <small class="font-weight-bold">{{ data.item.created_by_user.first_name }} {{ data.item.created_by_user.last_name }}</small>
          <small class="text-muted">{{ formatDate(data.item.created, 'dd MMM yyyy, h:mm a') }}</small>
        </div>
        <small v-else class="text-muted">—</small>
      </template>

      <template #cell(updated_by_user)="data">
        <div v-if="data.item.updated_by_user" class="d-flex flex-column">
          <small class="font-weight-bold">{{ data.item.updated_by_user.first_name }} {{ data.item.updated_by_user.last_name }}</small>
          <small class="text-muted">{{ formatDate(data.item.modified, 'dd MMM yyyy, h:mm a') }}</small>
        </div>
        <small v-else class="text-muted">—</small>
      </template>

      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item v-if="can('member_status:CanUpdate')" @click="onEdit(data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Edit</span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('member_status:CanDelete')" @click="onDelete(data.item._id)">
            <feather-icon icon="DeleteIcon" />
            <span class="align-middle ml-50">Delete</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </list-table>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AddOrUpdateMemberStatusModal from "@/@core/components/shared/church/AddOrUpdateMemberStatusModal.vue";

import { debounce, get } from "lodash";
import { formatDate } from "@core/utils/utils";
import { BLink, BButton, BModal, BRow, BCol, BTooltip, BAvatar, BMedia, BBadge, BDropdown, BDropdownItem } from "bootstrap-vue";

const watchHandler = {
  handler(){
    this.debounceFetchAllData()
  },
  immediate: false
}

const watchHandlerRequiresPageReset = {
  handler(){
    this.currentPage = 1;
    this.debounceFetchAllData()
  },
  immediate: false
}

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BModal,
    BMedia,
    BBadge,
    BButton,
    vSelect,
    BAvatar,
    BTooltip,
    ListTable,
    BDropdown,
    BDropdownItem,
    AddOrUpdateMemberStatusModal
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      errorMessage: "",
      records: [],

      search: "",
      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      tableColumns: [
        { key: 'title', label: "Status", sortable: false },
        { key: 'description', sortable: false },
        { key: 'created_by_user', label: 'Created By', sortable: false },
        { key: 'updated_by_user', label: 'Updated By', sortable: false },
        { key: 'actions' },
      ]
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    currentPage: watchHandler,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);

    const {
      search = "",
      page = 1,
      limit = this.perPage
    } = this.$route.query;

    this.search = search;
    this.perPage = +limit;
    this.currentPage = +page;

    this.debounceFetchAllData();
  },
  methods: {
    formatDate,
    async fetchAllData() {
      try {
        this.loading = true;
        this.errorMessage = "";

        const query = {
          limit: this.perPage,
          search: this.search || undefined,
          page: this.currentPage,
        }
        await this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.memberStatus.list(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords;
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },

    async onDelete(id){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Delete?',
          text: "Do you want to delete this record?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().mainService.memberStatus.delete(id);
        await this.fetchAllData()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Record deleted Successfully'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onEdit(update){
      this.updateData = update;
      this.isAddOrUpdateActive = true;
    },
    resetUpdateData(){
      if (this.updateData){
        this.updateData = null
      }
    },
  }
}
</script>
